import React from 'react';
import { Container, Nav, Navbar, Breadcrumb as BsBreadcrumb } from 'react-bootstrap';
import { useMatches } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faCog, faRightFromBracket, faUser } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { NavLogo } from '../logos/NavLogo';
import { useAuth } from '../../../context/AuthContext';

const StyledIcon = styled(FontAwesomeIcon)`
  margin-right: 5px;
`;

const StyledBreadcrumb = styled(BsBreadcrumb)`
  margin: 0;
  margin-left: 15px;

  .breadcrumb-item + .breadcrumb-item::before {
    color: ${(props) => props.theme.colors.mutedText};
  }
`;

interface RouteHandle {
  // A translation key (e.g. "topNav.settings"), not literal display text —
  // routes are declared once but need to render in whichever language is
  // currently active.
  breadcrumb?: string;
}

// Routes declare their own crumb via `handle: { breadcrumb: "topNav.xyz" }`
// (see App.tsx) — this flat route tree only ever matches one route per URL,
// so there's at most one extra crumb beyond the "Celestial Octopus" home
// link already in the brand slot. The `useMatches` matches are still walked
// in full (rather than just reading `matches[0]`) so nested routes would
// grow the trail correctly if this tree ever stops being flat.
const useCurrentBreadcrumbKey = (): string | undefined => {
  const matches = useMatches();
  return matches
    .map((match) => (match.handle as RouteHandle | undefined)?.breadcrumb)
    .filter((key): key is string => Boolean(key))
    .pop();
};

const TopNav = () => {
  const { logout } = useAuth();
  const { t } = useTranslation();
  const breadcrumbKey = useCurrentBreadcrumbKey();

  return (
    <Navbar bg="dark" data-bs-theme="dark" expand="md">
      <Container>
        <Navbar.Brand href="/">
          <NavLogo/>
          Celestial Octopus
        </Navbar.Brand>

        {breadcrumbKey && (
          <StyledBreadcrumb>
            <BsBreadcrumb.Item href="/">{t('topNav.home')}</BsBreadcrumb.Item>
            <BsBreadcrumb.Item active>{t(breadcrumbKey)}</BsBreadcrumb.Item>
          </StyledBreadcrumb>
        )}

        <Navbar.Toggle aria-controls="top-nav-links"/>
        <Navbar.Collapse id="top-nav-links">
          <Nav className="ms-auto">
            <Nav.Link href="/settings">
              <StyledIcon icon={faCog} />
              {t('topNav.settings')}
            </Nav.Link>
            <Nav.Link href="/user">
              <StyledIcon icon={faUser} />
              {t('topNav.user')}
            </Nav.Link>
            <Nav.Link href="/help">
              <StyledIcon icon={faCircleQuestion} />
            </Nav.Link>
            <Nav.Link onClick={() => logout()}>
              <StyledIcon icon={faRightFromBracket} />
              {t('topNav.logOut')}
            </Nav.Link>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export {
  TopNav,
};
